import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setInvestmentAmounts,
  setnoOfFDYear,
  setIncrementPerYear,
  SetSumAmount,
  resetFdData,
} from "../redux/calculations/fdSlice";
import {
  resetChartData,
  updatedFdChart,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import arrowImage from "../assets/svg/arrow.svg";
import { useTranslation } from "react-i18next";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import DecimalUtils from "../utiles/decimalFormate";
import { Helmet } from "react-helmet";

const FdCalc = () => {
  const { t } = useTranslation();
  const [calcDone, setCalcDone] = useState(false);
  const dispatch = useDispatch();
  const { investmentAmmount, noOfFDYear, incrementPerYear } = useSelector(
    (state) => state.fdCalculator
  );
  const { textData } = useLocale();
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [timeUnit, setTimeUnit] = useState("Years");
  const [showTable, setShowTable] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const validationSchema = Yup.object({
    investmentAmmount: Yup.number()
      .required(textData.fdInvestrequired)
      .min(5000, textData.investRange)
      .max(2000000, textData.investRange),
    noOfFDYear: Yup.number()
      .required(textData.fdTimeRequired)
      .min(
        timeUnit === "Years" ? 1 : timeUnit === "Months" ? 1 : 1,
        timeUnit === "Years"
          ? textData.fdTimeYear
          : timeUnit === "Months"
            ? textData.fdTimeMonth
            : textData.fdTimeDay
      )
      .max(
        timeUnit === "Years" ? 10 : timeUnit === "Months" ? 11 : 31,
        timeUnit === "Years"
          ? textData.fdTimeYear
          : timeUnit === "Months"
            ? textData.fdTimeMonth
            : textData.fdTimeDay
      ),
    incrementPerYear: Yup.number()
      .required(textData.fdInterestRequired)
      .min(1, textData.fdInterestMin)
      .max(10, textData.fdInterestMax),
  });

  const formik = useFormik({
    initialValues: {
      investmentAmmount: investmentAmmount,
      noOfFDYear: noOfFDYear,
      incrementPerYear: incrementPerYear,
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_FD");
      }

      try {
        const P = Number(values.investmentAmmount);
        const R = Number(values.incrementPerYear);
        const T = Number(values.noOfFDYear);
        const tenurePeriodType = timeUnit.toLowerCase();
        const compounding =
          values.compoundingFrequency?.toLowerCase() || "quarterly";

        const compoundingMap = { quarterly: 4, "half-yearly": 2, yearly: 1 };
        const n = compoundingMap[compounding] || 4;

        // Validate inputs
        if (isNaN(P) || isNaN(R) || isNaN(T) || P <= 0 || R <= 0 || T <= 0) {
          throw new Error(
            "Invalid input: Enter valid numbers greater than zero."
          );
        }

        // Convert tenure to years
        let timeInYears;
        switch (tenurePeriodType) {
          case "days":
            timeInYears = T / 365;
            break;
          case "months":
            timeInYears = T / 12;
            break;
          case "years":
          default:
            timeInYears = T;
            break;
        }

        const maturityAmount = P * Math.pow(1 + R / (n * 100), n * timeInYears);
        const totalInterestEarned = maturityAmount - P;

        dispatch(SetSumAmount(maturityAmount));
        dispatch(
          updatedFdChart({
            investAmount: P,
            estReturn: totalInterestEarned,
            totalAmount: maturityAmount,
          })
        );

        // Save history
        saveToHistory({
          tabName: "FDCalc",
          totalInvestment: P,
          yearlyReturn: totalInterestEarned,
          interestRate: R,
          tenure: T,
          actualTenureInYears: timeInYears,
          tenureUnit: timeUnit,
          maturityAmount,
          calculationType: "FD",
        });
      } catch (error) {
        // Optionally: Show user-facing error message
      }
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
    },
  });

  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.yearlyReturn,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        tenureUnit: calculationData.tenureUnit,
        maturityAmount: calculationData.maturityAmount,
        actualTenureInYears: calculationData.actualTenureInYears,
        calculationType: calculationData.calculationType,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.tenureUnit === newHistoryRecord.tenureUnit &&
          Math.abs(record.maturityAmount - newHistoryRecord.maturityAmount) <
          0.01 // Allow small floating point differences
        );
      });

      // Only add the new record if it's not a duplicate
      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) { }
  };
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetFdData());
    };
  }, [dispatch]);

  const handleInvestmentChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    ); // Convert to a number
    if (rawValue.length > 11) return;
    if (rawValue === "") {
      formik.setFieldValue("investmentAmmount", "");
      dispatch(setInvestmentAmounts(""));
      return;
    }
    if (rawValue.match(/^[.,]+$/)) {
      return;
    }
    formik.setFieldValue("investmentAmmount", numericValue);
    dispatch(setInvestmentAmounts(numericValue));
    setIsFormModified(true);
    setCalcDone(false);
  };

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);

  const handleTenureSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setnoOfFDYear(value));
    formik.setFieldValue("noOfFDYear", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIncrementPerYear(value));
    formik.setFieldValue("incrementPerYear", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInvestmentSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setInvestmentAmounts(value));
    formik.setFieldValue("investmentAmmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };
  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue === "") {
      formik.setFieldValue("incrementPerYear", "");
      return;
    }
    if (rawValue.length > 5) return;
    if (!DecimalUtils.getValidationPattern().test(rawValue)) return;
    const normalizedValue = DecimalUtils.normalizeInput(rawValue);

    dispatch(setIncrementPerYear(normalizedValue));
    formik.setFieldValue("incrementPerYear", normalizedValue);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("noOfFDYear", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setnoOfFDYear(clamped));
    }
    setIsFormModified(true);
    setCalcDone(false);
  };

  const toggleTimeUnit = () => {
    let newValue, newUnit;

    if (timeUnit === "Years") {
      newUnit = "Months";
      // Convert years to months
      newValue = Math.round(5);
    } else if (timeUnit === "Months") {
      newUnit = "Days";
      // Convert months to days
      newValue = Math.round(5);
    } else {
      newUnit = "Years";
      // Convert days to years
      newValue = Math.round(5);
    }

    setTimeUnit(newUnit);
    formik.setFieldValue("noOfFDYear", newValue);
    dispatch(setnoOfFDYear(newValue));
    setIsFormModified(true);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";

    return isNaN(Number(value))
      ? ""
      : Number(value).toLocaleString(
        shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
      );
  };

  useEffect(() => {
    if (formik.values.investmentAmmount !== undefined) {
      dispatch(setInvestmentAmounts(formik.values.investmentAmmount));
    }
    dispatch(setnoOfFDYear(formik.values.noOfFDYear));
    dispatch(setIncrementPerYear(formik.values.incrementPerYear));
  }, [formik.values, dispatch]);

  // Update slider max values based on time unit
  const getSliderMax = () => {
    switch (timeUnit) {
      case "Months":
        return 11;
      case "Days":
        return 30;
      default:
        return 25;
    }
  };

  // Update slider step based on time unit
  const getSliderStep = () => {
    switch (timeUnit) {
      case "Days":
        return 1;
      default:
        return 1;
    }
  };

  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {" "}
        {textData.fdContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.fdContent.description}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {" "}
        {textData.fdContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {" "}
        {textData.fdContent.howItWorksDescription}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        A = P x (1 + r/n)<sup>nt</sup>
      </p>

      <table className="border-collapse mt-4 w-full">
        <thead></thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.fdContent.formulaDescription.P}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              r
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.fdContent.formulaDescription.r}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              n
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.fdContent.formulaDescription.n}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              t
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.fdContent.formulaDescription.t}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {" "}
        {textData.fdContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.fdContent.benefitsList[0]}</li>
        <li>{textData.fdContent.benefitsList[1]}</li>
        <li>{textData.fdContent.benefitsList[2]}</li>
        <li>{textData.fdContent.benefitsList[3]}</li>
        <li>{textData.fdContent.benefitsList[4]}</li>
        <li>{textData.fdContent.benefitsList[5]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.fdContent.exampleTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.fdContent.exampleDescription}
      </p>
      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        A = 100000 × (1 + 6.5 / (4 × 100))<sup>4 × 5</sup>
      </p>
      <p className="font-medium  text-sm sm:text-base p-3 mt-3">
        {textData.fdContent.return}
      </p>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>
          <strong> {textData.fdContent.exampleValues[0].label}:</strong>{" "}
          {textData.currency}1,00,000
        </li>
        <li>
          <strong> {textData.fdContent.exampleValues[1].label}:</strong> ~
          {textData.currency}1,38,915
        </li>
        <li>
          <strong> {textData.fdContent.exampleValues[2].label}:</strong> ~
          {textData.currency}38,915
        </li>
      </ul>
    </div>
  );

  const timeLabel =
    timeUnit === "Years"
      ? t("yearShort")
      : timeUnit === "Months"
        ? t("month").charAt(0).toUpperCase()
        : t("day").charAt(0).toUpperCase();

  return (
    <>
      <Helmet>
        <title>GrowLifeFund Fixed Deposit Calculator | Calculate FD Returns</title>
        <meta
          name="description"
          content="Fixed Deposit Returns Calculator by GrowLifeFund helps you calculate FD interest rates, maturity amount, and plan secure FD investments with confidence."
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/fixed-deposit-calculator"
        />

        <meta
          property="og:title"
          content="GrowLifeFund Fixed Deposit Calculator | Calculate FD Returns"
        />
        <meta
          property="og:description"
          content="Fixed Deposit Returns Calculator by GrowLifeFund helps you calculate FD interest rates, maturity amount, and plan secure FD investments with confidence."
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/fixed-deposit-calculator"
        />
        <meta
          property="og:image:alt"
          content="Fixed Deposit Calculator Online - GrowLifeFund"
        />

        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        title={
          <>
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.fixedDeposit}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.sipCalc.return}
            </span>
          </>
        }
        description={t("descriptionfd")}
        timeUnit={timeUnit}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.totalInvestmentAmount}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={sliderTrackRef}>

                  <input
                    type="range"
                    min={5000}
                    max={2000000}
                    step={5000}
                    value={investmentAmmount}
                    onChange={handleInvestmentSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((investmentAmmount - 5000) / (2000000 - 5000)) * 100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="investmentAmmount"
                    leftIcon={textData.currency}
                    value={formatToINR(formik.values.investmentAmmount)}
                    onChange={handleInvestmentChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    placeholder={getPlaceholderValue(i18n.language)}
                    dir="ltr"
                    borderColor={
                      formik.touched.investmentAmmount &&
                        formik.errors.investmentAmmount
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`w-[104px] sm:w-[112px] ${formik.touched.investmentAmmount &&
                      formik.errors.investmentAmmount
                      ? "border-danger"
                      : "border-gray-light"
                      }`} className={`" sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                  />
                  {formik.touched.investmentAmmount &&
                    formik.errors.investmentAmmount && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.investmentAmmount}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full  " ref={interestSliderRef}>

                  <input
                    type="range"
                    min={1}
                    max={10}
                    step={0.1}
                    value={
                      // Only use numeric values for the slider, fallback to previous valid value (e.g., 1)
                      !isNaN(parseFloat(formik.values.incrementPerYear))
                        ? parseFloat(formik.values.incrementPerYear)
                        : 1
                    }
                    onChange={handleInterestSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${((formik.values.incrementPerYear - 1) / (10 - 1)) * 100
                        }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="incrementPerYear"
                    leftIcon="%"
                    type="text"
                    placeholder={DecimalUtils.getPlaceholder("6.5")}
                    value={DecimalUtils.formatDisplay(
                      formik.values.incrementPerYear
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.incrementPerYear &&
                        formik.errors.incrementPerYear
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`" sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                  />
                  {formik.touched.incrementPerYear &&
                    formik.errors.incrementPerYear && (
                      <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                        {formik.errors.incrementPerYear}
                      </div>
                    )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start flex gap-1  sm:gap-[10px]  font-normal md:text-sm text-black sm:text-sm text-sm flex-row">
                  {textData.timePeriod}

                  <div
                    onClick={toggleTimeUnit}
                    className=" flex items-center cursor-pointer"
                  >
                    <div className="relative flex items-center gap-[2px]">
                      <div className="text-primary  sm:text-sm  text-sm  font-normal">
                        {timeUnit === "Years"
                          ? t("years")
                          : timeUnit === "Months"
                            ? t("months")
                            : t("days")}
                      </div>
                      <div className="absolute -bottom-1 left-0 right-0 h-0.5 "></div>
                      <img
                        src={arrowImage}
                        alt="Dropdown arrow"
                        className="w-3 h-3 sm:w-4 sm:h-4"
                      />
                    </div>
                  </div>
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full  " ref={periodSliderRef}>

                  <input
                    type="range"
                    min={
                      timeUnit === "Years" ? 1 : timeUnit === "Months" ? 1 : 1
                    }
                    max={
                      timeUnit === "Years"
                        ? 10
                        : timeUnit === "Months"
                          ? 11
                          : 31
                    }
                    step={1}
                    value={noOfFDYear}
                    onChange={handleTenureSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val":
                        timeUnit === "Years"
                          ? `${((noOfFDYear - 1) / (10 - 1)) * 100}%`
                          : timeUnit === "Months"
                            ? `${((noOfFDYear - 1) / (11 - 1)) * 100}%`
                            : `${((noOfFDYear - 1) / (31 - 1)) * 100}%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="noOfFDYear"
                    leftIcon={timeLabel}
                    value={formik.values.noOfFDYear}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={
                      timeUnit === "Years"
                        ? "5"
                        : timeUnit === "Months"
                          ? "60"
                          : "5"
                    }
                    borderColor={
                      formik.touched.noOfFDYear && formik.errors.noOfFDYear
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className={`" sm:h-[36px] h-[32px] flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"`}
                  />
                  {formik.touched.noOfFDYear && formik.errors.noOfFDYear && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.noOfFDYear}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default FdCalc;
